import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

interface StatCardProps {
  value: number | string;
  label: string;
  icon: ReactNode;
  colorMain: string;
  colorBg: string;
  hint?: string;
}

export function StatCard({ value, label, icon, colorMain, colorBg, hint }: StatCardProps) {
  return (
    <Box
      bgcolor={colorBg}
      p={2.5}
      borderRadius={3}
      border="1px solid #e2e8f0"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${colorBg}00 0%, ${colorBg}40 100%)`,
          opacity: 0,
          transition: "opacity 0.3s ease",
        },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 24px ${colorBg}60`,
          borderColor: colorMain,
          "&::before": {
            opacity: 1,
          },
          "& .stat-icon": {
            transform: "scale(1.1) rotate(5deg)",
          },
          "& .stat-value": {
            transform: "scale(1.05)",
          },
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, minWidth: 0, pr: 1 }}>
        <Typography
          className="stat-value"
          fontSize="1.75rem"
          fontWeight={900}
          color={colorMain}
          sx={{
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {value}
        </Typography>
        <Typography
          fontSize="0.625rem"
          fontWeight={700}
          color={colorMain}
          textTransform="uppercase"
          letterSpacing={1.5}
        >
          {label}
        </Typography>
        {hint && (
          <Typography
            fontSize="0.75rem"
            fontWeight={500}
            color="text.secondary"
            lineHeight={1.35}
            mt={0.5}
          >
            {hint}
          </Typography>
        )}
      </Box>
      <Box
        className="stat-icon"
        width={40}
        height={40}
        bgcolor={colorMain}
        borderRadius={1.5}
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          position: "relative",
          zIndex: 1,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {icon}
      </Box>
    </Box>
  );
}
